import { useEffect, useMemo, useState } from "react";
import { CalendarDays, Clock, ExternalLink, Loader2, MessageSquare, Radio } from "lucide-react";
import FlagEmojiText from "../components/FlagEmojiText";
import PublicSiteLayout, { PageHero, PublicLinkButton, PUBLIC_LINKS } from "../components/PublicSiteLayout";
import { api } from "../lib/api";

function formatDate(value) {
  if (!value) return "Date coming soon";
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch (err) {
    return value;
  }
}

function formatTime(value) {
  if (!value) return "";
  try {
    return new Intl.DateTimeFormat(undefined, { timeStyle: "short" }).format(new Date(value));
  } catch (err) {
    return value;
  }
}

function dayKey(value) {
  if (!value) return "Coming soon";
  try {
    return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(new Date(value));
  } catch (err) {
    return "Coming soon";
  }
}

export default function PublicSchedule() {
  const [items, setItems] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await api.get("/schedule");
        if (!mounted) return;
        setItems(data.items || []);
        setCurrent(data.current || null);
      } catch (err) {
        if (mounted) setError(err.response?.data?.detail || "Could not load the schedule right now.");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  const grouped = useMemo(() => {
    return items.reduce((acc, item) => {
      const key = dayKey(item.start_time);
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});
  }, [items]);

  return (
    <PublicSiteLayout>
      <PageHero
        eyebrow="Stream schedule"
        title="What is coming up."
        body="Upcoming streams, community nights, creator segments, and special plans. The schedule stays platform-flexible so Parker can use it across Twitch, Kick, YouTube, Discord, and future community tools."
        actions={(
          <>
            <PublicLinkButton href={PUBLIC_LINKS.twitch} primary>
              <Radio className="w-4 h-4" /> Watch live
            </PublicLinkButton>
            <PublicLinkButton href={PUBLIC_LINKS.discord}>
              <MessageSquare className="w-4 h-4" /> Join Discord
            </PublicLinkButton>
          </>
        )}
      />

      {error && (
        <section className="mt-8 rounded border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-100">
          {error}
        </section>
      )}

      {current && (
        <section className="mt-10 surface bg-black/75 p-5 sm:p-7 border-[hsl(var(--currency-event))]/50">
          <div className="label-eyebrow text-[hsl(var(--currency-event))] flex items-center gap-2">
            <Radio className="w-4 h-4" /> Current / active
          </div>
          <div className="mt-4 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div>
              <h2 className="text-3xl font-black uppercase tracking-normal leading-tight text-[hsl(var(--foreground))]">
                <FlagEmojiText value={current.title} />
              </h2>
              <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed max-w-3xl">{current.description || "Happening now or marked active by Parker."}</p>
              <div className="mt-4 flex flex-wrap gap-3 text-sm text-[hsl(var(--muted-foreground))]">
                <span className="inline-flex items-center gap-2"><Clock className="w-4 h-4" /> {formatDate(current.start_time)}</span>
                {current.end_time && <span>Ends {formatTime(current.end_time)}</span>}
              </div>
            </div>
            <ScheduleBadge item={current} />
          </div>
        </section>
      )}

      <section className="mt-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
          <div>
            <div className="label-eyebrow text-[hsl(var(--currency-event))]">Upcoming</div>
            <h2 className="text-2xl font-semibold tracking-tight text-[hsl(var(--foreground))] mt-1">Schedule</h2>
          </div>
          <div className="text-sm text-[hsl(var(--muted-foreground))]">
            Times are shown in your browser's local timezone.
          </div>
        </div>

        {loading ? (
          <div className="surface bg-black/75 p-6 text-sm text-[hsl(var(--muted-foreground))] flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading schedule...
          </div>
        ) : items.length === 0 ? (
          <div className="surface bg-black/75 p-6 sm:p-7 text-sm text-[hsl(var(--muted-foreground))]">
            <CalendarDays className="w-8 h-8 text-[hsl(var(--currency-event))] mb-3" strokeWidth={1.5} />
            <h3 className="text-xl font-semibold tracking-tight text-[hsl(var(--foreground))]">No public schedule posted yet.</h3>
            <p className="mt-2 max-w-2xl leading-relaxed">
              Check Discord for stream plans and community night announcements while the schedule fills in.
            </p>
            <div className="mt-5">
              <PublicLinkButton href={PUBLIC_LINKS.discord}>
                <MessageSquare className="w-4 h-4" /> Join Discord
              </PublicLinkButton>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(grouped).map(([day, dayItems]) => (
              <div key={day}>
                <h3 className="text-sm uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-3">{day}</h3>
                <div className="space-y-3">
                  {dayItems.map((item) => <ScheduleCard key={item.id} item={item} />)}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </PublicSiteLayout>
  );
}

function ScheduleCard({ item }) {
  return (
    <article className="surface bg-black/75 p-5 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
      <div className="min-w-0">
        <div className="label-eyebrow flex items-center gap-2" style={{ color: item.color || "#BF5AF2" }}>
          <CalendarDays className="w-4 h-4" /> {item.category || "Schedule"}
        </div>
        <h3 className="mt-2 text-2xl font-black uppercase tracking-normal leading-tight text-[hsl(var(--foreground))] break-words">
          <FlagEmojiText value={item.title} />
        </h3>
        {item.description && (
          <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed max-w-3xl">{item.description}</p>
        )}
        <div className="mt-4 flex flex-wrap gap-3 text-sm text-[hsl(var(--muted-foreground))]">
          <span className="inline-flex items-center gap-2"><Clock className="w-4 h-4" /> {formatDate(item.start_time)}</span>
          {item.end_time && <span>Ends {formatTime(item.end_time)}</span>}
          {item.timezone && <span>{item.timezone}</span>}
        </div>
      </div>
      <div className="flex flex-wrap lg:flex-col gap-2 lg:items-end">
        <ScheduleBadge item={item} />
        {item.link && (
          <a href={item.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-[hsl(var(--currency-event))] hover:text-white">
            Open link <ExternalLink className="w-4 h-4" />
          </a>
        )}
      </div>
    </article>
  );
}

function ScheduleBadge({ item }) {
  return (
    <span className="inline-flex items-center gap-2 rounded border px-3 py-2 text-xs uppercase tracking-widest text-[hsl(var(--foreground))] bg-black/40" style={{ borderColor: item.color || "#BF5AF2" }}>
      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color || "#BF5AF2" }} />
      {item.current ? "Now" : item.category || "Scheduled"}
    </span>
  );
}
